import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { hierarchyViewIdSearchParamKey } from "./utils/constants/constants";
import { ViewsHierarchyMoreTabsDropdown } from "./components/more-views-dropdown/ViewsHierarchyMoreTabsDropdown";
import { AddViewInHierarchyButton } from "./components/adding-view/AddViewInHierarchyButton";
import { ViewsHierarchyTabs } from "./components/ViewsHierarchyTabs";
import {
  useViewsHierarchyContext,
  ViewsHierarchyProvider,
  ViewsHierarchyProviderProps,
} from "./components/ViewsHierarchyProvider";
import { getDividedViews } from "./utils/helpers/getDividedViews";
import { NavigationExpander } from "../navigation-expander/NavigationExpander";
import { useWindowResizeDebounced } from "../../../utils/hooks/useWindowResizeDebounced.hook";
import { estimateTabWidth } from "@/components/pages/common/views-hierarchy-tabs/utils/helpers/estimateTabWidth";

interface ViewsHierarchyComponentProps {
  onTabChange(viewId: string): void;
  disableReorder?: boolean;
}

export const ViewsHierarchyComponent: FC<ViewsHierarchyComponentProps> = ({
  onTabChange,
  disableReorder,
}) => {
  const [searchParams] = useSearchParams();
  const selectedViewId = searchParams.get("viewId") ?? "";
  const { pathname, views } = useViewsHierarchyContext();
  const { rerenderKey } = useWindowResizeDebounced();

  const navigate = useNavigate();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [fittingViewsCount, setFittingViewsCount] = useState(0);

  const dividedViews = useMemo(
    () => getDividedViews(views, selectedViewId, fittingViewsCount),
    [fittingViewsCount, selectedViewId, views],
  );

  const tabChangeHandler = useCallback(
    (viewId: string) => {
      if (viewId === selectedViewId) {
        return;
      }

      if (!viewId) {
        navigate(pathname);
      } else {
        navigate(`${pathname}?${hierarchyViewIdSearchParamKey}=${viewId}`);
      }

      onTabChange(viewId);
    },
    [navigate, selectedViewId, onTabChange, pathname],
  );

  useEffect(() => {
    if (!dividedViews.allViews.length) {
      return;
    }

    //Approximate numbers
    const allItemsWidth = 120;
    const moreIconWidth = 75;
    const addIconWidth = 90;
    const width =
      (wrapperRef.current?.clientWidth ?? 0) -
      allItemsWidth -
      moreIconWidth -
      addIconWidth;

    if (width < 0) {
      return;
    }

    let fittingItemsWidth = 0;
    let fittingItemsCount = 0;

    for (const view of dividedViews.allViews) {
      const nextItemWidth = estimateTabWidth(view);

      if (fittingItemsWidth + nextItemWidth < width) {
        fittingItemsWidth += nextItemWidth;
        fittingItemsCount++;
      } else {
        break;
      }
    }

    setFittingViewsCount(Math.max(fittingItemsCount, 1));
  }, [dividedViews.allViews, rerenderKey]);

  const needToShowMoreButton = !!dividedViews.hiddenViews.length;

  return (
    <Box borderBottom={1} borderColor="grey.300">
      <Stack width="95%" spacing={2} direction="row" alignItems="center">
        <NavigationExpander />

        <Stack
          flex={1}
          direction="row"
          overflow="hidden"
          ref={wrapperRef}
          alignItems="center"
        >
          <ViewsHierarchyTabs
            views={dividedViews.visibleViews}
            onTabChange={tabChangeHandler}
            selectedViewId={selectedViewId}
          />

          {needToShowMoreButton && (
            <ViewsHierarchyMoreTabsDropdown
              onTabChange={tabChangeHandler}
              selectedViewId={selectedViewId}
              hiddenViewsCount={dividedViews.hiddenViews.length}
              disableReorder={disableReorder}
            />
          )}

          <AddViewInHierarchyButton />
        </Stack>
      </Stack>
    </Box>
  );
};

type ViewsHierarchyProps = ViewsHierarchyComponentProps &
  ViewsHierarchyProviderProps;

export const ViewsHierarchy: FC<ViewsHierarchyProps> = ({
  onTabChange,
  ...props
}) => {
  return (
    <ViewsHierarchyProvider {...props}>
      <ViewsHierarchyComponent
        onTabChange={onTabChange}
        disableReorder={props.actions.viewReorder.disabled}
      />
    </ViewsHierarchyProvider>
  );
};
